/**
 * Expanded Question Bank: Domain 5 — Working Memory & Retention (الذاكرة العاملة والاستبقاء)
 * 15 Stochastic Working Memory & Sequential Manipulation Templates
 */

import { renderOptionSvg, DrawShapes } from './svg-builder.js';

export const EXPANDED_MEMORY_TEMPLATES = [
  // 1. Symbol Coordinate Recall
  {
    id: 'mem_symbol_coordinate_recall',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const symbols = ['▲', '■', '●', '★', '◆'];
      const positions = [
        { r: 1, c: 2, name_ar: 'الصف 1 ، العمود 2' },
        { r: 2, c: 3, name_ar: 'الصف 2 ، العمود 3' },
        { r: 3, c: 1, name_ar: 'الصف 3 ، العمود 1' },
        { r: 2, c: 1, name_ar: 'الصف 2 ، العمود 1' }
      ];

      const chosenSyms = prng.sample(symbols, 4);
      const targetIdx = prng.int(0, 3);
      const targetPos = positions[targetIdx];
      const correctSym = chosenSyms[targetIdx];

      const otherSyms = symbols.filter(s => s !== correctSym);
      const distractors = prng.sample(otherSyms, 3);

      const allChoices = prng.shuffle([correctSym, ...distractors]);
      const correctIndex = allChoices.indexOf(correctSym);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">توزعت الرموز في شبكة من 3 صفوف و 3 أعمدة كالتالي:</div>
          <div class="iq-stimulus-card-highlight">
            [ ${chosenSyms[0]} في ${positions[0].name_ar} ] &nbsp;•&nbsp; [ ${chosenSyms[1]} في ${positions[1].name_ar} ]<br>
            [ ${chosenSyms[2]} في ${positions[2].name_ar} ] &nbsp;•&nbsp; [ ${chosenSyms[3]} في ${positions[3].name_ar} ]
          </div>
          <div class="iq-stimulus-card-muted">المطلوب: استرجاع الرمز المستقر في الموضع [ ${targetPos.name_ar} ]</div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">Symbols distributed across a 3×3 grid:</div>
          <div class="iq-stimulus-card-highlight">
            [ ${chosenSyms[0]} at Row ${positions[0].r}, Col ${positions[0].c} ] &nbsp;•&nbsp; [ ${chosenSyms[1]} at Row ${positions[1].r}, Col ${positions[1].c} ]<br>
            [ ${chosenSyms[2]} at Row ${positions[2].r}, Col ${positions[2].c} ] &nbsp;•&nbsp; [ ${chosenSyms[3]} at Row ${positions[3].r}, Col ${positions[3].c} ]
          </div>
          <div class="iq-stimulus-card-muted">Target: Retrieve the symbol positioned at [ Row ${targetPos.r}, Col ${targetPos.c} ]</div>
        </div>
      `;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `بناءً على توزيع الرموز في الشبكة، ما هو الرمز الذي كان مستقراً في الموضع (${targetPos.name_ar})؟`,
        questionText_en: `Based on the grid distribution, which symbol was positioned at (Row ${targetPos.r}, Column ${targetPos.c})?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options: allChoices.map((sym, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: sym,
          text_en: sym,
          value: sym
        })),
        correctIndex,
        explanation_ar: `الرمز المقترن بهذا الموضع في التوزيع المعروض هو: ${correctSym}.`,
        explanation_en: `The symbol recorded at that coordinate was: ${correctSym}.`
      };
    }
  },

  // 2. Color Sequence Strict Reverse Recall
  {
    id: 'mem_color_sequence_reverse',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const colors = [
        { name_ar: 'أحمر', name_en: 'Red', hex: '#ef4444' },
        { name_ar: 'أزرق', name_en: 'Blue', hex: '#38bdf8' },
        { name_ar: 'أخضر', name_en: 'Green', hex: '#10b981' },
        { name_ar: 'أصفر', name_en: 'Yellow', hex: '#f59e0b' },
        { name_ar: 'بنفسجي', name_en: 'Purple', hex: '#a855f7' }
      ];

      const seq = prng.sample(colors, 4);
      const reversedSeq = [...seq].reverse();

      const correctAns_ar = reversedSeq.map(c => c.name_ar).join(' ← ');
      const correctAns_en = reversedSeq.map(c => c.name_en).join(' -> ');

      // Distractors: wrong reverses or slight swaps
      const dist1_ar = [...seq].map(c => c.name_ar).join(' ← ');
      const dist1_en = [...seq].map(c => c.name_en).join(' -> ');

      const swapRev = [...reversedSeq];
      const tmp = swapRev[1];
      swapRev[1] = swapRev[2];
      swapRev[2] = tmp;
      const dist2_ar = swapRev.map(c => c.name_ar).join(' ← ');
      const dist2_en = swapRev.map(c => c.name_en).join(' -> ');

      const swapRev2 = [...reversedSeq];
      const tmp2 = swapRev2[0];
      swapRev2[0] = swapRev2[1];
      swapRev2[1] = tmp2;
      const dist3_ar = swapRev2.map(c => c.name_ar).join(' ← ');
      const dist3_en = swapRev2.map(c => c.name_en).join(' -> ');

      const allChoices = prng.shuffle([
        { ar: correctAns_ar, en: correctAns_en, isCorrect: true },
        { ar: dist1_ar, en: dist1_en, isCorrect: false },
        { ar: dist2_ar, en: dist2_en, isCorrect: false },
        { ar: dist3_ar, en: dist3_en, isCorrect: false }
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      let discsSvgAr = '';
      let discsSvgEn = '';
      seq.forEach((c, i) => {
        discsSvgAr += `<circle cx="${70 + i * 80}" cy="45" r="22" fill="${c.hex}" stroke="#f8fafc" stroke-width="2"/>`;
        discsSvgAr += `<text x="${70 + i * 80}" y="88" fill="#f8fafc" font-size="12" font-weight="700" text-anchor="middle">${c.name_ar}</text>`;

        discsSvgEn += `<circle cx="${70 + i * 80}" cy="45" r="22" fill="${c.hex}" stroke="#f8fafc" stroke-width="2"/>`;
        discsSvgEn += `<text x="${70 + i * 80}" y="88" fill="#f8fafc" font-size="12" font-weight="700" text-anchor="middle">${c.name_en}</text>`;
      });

      const stimulusHtml_ar = `<svg viewBox="0 0 380 110" xmlns="http://www.w3.org/2000/svg">
        <rect width="380" height="110" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255,255,255,0.1)"/>
        ${discsSvgAr}
      </svg>`;

      const stimulusHtml_en = `<svg viewBox="0 0 380 110" xmlns="http://www.w3.org/2000/svg">
        <rect width="380" height="110" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255,255,255,0.1)"/>
        ${discsSvgEn}
      </svg>`;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `إذا أردت استرجاع تسلسل الألوان المعروضة بالترتيب العكسي التام (من الأخير إلى الأول)، فما هو الترتيب الصحيح؟`,
        questionText_en: `What is the correct sequence of the displayed colors in exact reverse order (last to first)?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `الترتيب العكسي يبدأ من العنصر الأخير (${reversedSeq[0].name_ar}) وصولاً للأول (${reversedSeq[3].name_ar}): ${correctAns_ar}.`,
        explanation_en: `Reversing the sequence gives: ${correctAns_en}.`
      };
    }
  },

  // 3. Grid Breadcrumb Path Navigation
  {
    id: 'mem_grid_path_breadcrumbs',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // Start at (1, 1). Grid is 4x4 from (1,1) top-left to (4,4) bottom-right
      let r = 1;
      let c = 1;
      const steps_ar = [];
      const steps_en = [];

      // Generate 4 valid moves
      const moves = ['R', 'D', 'R', 'D'];
      prng.shuffle(moves).forEach(m => {
        if (m === 'R') {
          c++;
          steps_ar.push('خطوة لليمين');
          steps_en.push('One step Right');
        } else {
          r++;
          steps_ar.push('خطوة للأسفل');
          steps_en.push('One step Down');
        }
      });

      const correctAns_ar = `الصف ${r} ، العمود ${c}`;
      const correctAns_en = `Row ${r}, Column ${c}`;

      const candidateCoords = [
        { r: r - 1, c: c },
        { r: r, c: c - 1 },
        { r: Math.min(4, r + 1), c: c },
        { r: r, c: Math.min(4, c + 1) },
        { r: 1, c: 1 },
        { r: 2, c: 2 }
      ];

      const distSet = new Set();
      const distractors = [];
      for (const cd of candidateCoords) {
        if (cd.r >= 1 && cd.r <= 4 && cd.c >= 1 && cd.c <= 4 && !(cd.r === r && cd.c === c)) {
          const key = `${cd.r},${cd.c}`;
          if (!distSet.has(key)) {
            distSet.add(key);
            distractors.push({
              ar: `الصف ${cd.r} ، العمود ${cd.c}`,
              en: `Row ${cd.r}, Column ${cd.c}`
            });
            if (distractors.length === 3) break;
          }
        }
      }

      const allChoices = prng.shuffle([
        { ar: correctAns_ar, en: correctAns_en, isCorrect: true },
        ...distractors
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">نقطة البداية: الخلية [ الصف 1 ، العمود 1 ] في شبكة (4×4)</div>
          <div class="iq-stimulus-card-body">مسار الحركة: ${steps_ar.join(' ← ')}</div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">Starting Point: Cell [ Row 1, Column 1 ] in a 4×4 Grid</div>
          <div class="iq-stimulus-card-body">Movement Path: ${steps_en.join(' → ')}</div>
        </div>
      `;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `إذا انطلقت نقطة من الخلية (1, 1) وتتبعت مسار الحركة الموضح خطوة بخطوة، فأين تستقر في النهاية؟`,
        questionText_en: `Following the exact movement sequence starting at cell (1, 1), at which coordinates does the marker land?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options: allChoices.map((ch, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: ch.ar,
          text_en: ch.en,
          value: ch.ar
        })),
        correctIndex,
        explanation_ar: `بتتبع الحركات: زادت الصفوف بمقدار ${r - 1} والأعمدة بمقدار ${c - 1} لتصل إلى: ${correctAns_ar}.`,
        explanation_en: `Tracing row and column increments yields the destination: ${correctAns_en}.`
      };
    }
  },

  // 4. Mental State Inversion Tracker
  {
    id: 'mem_transformation_state_tracker',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      // Initial: White Triangle pointing UP
      // Operation 1: Rotate 180° (points DOWN)
      // Operation 2: Invert color (becomes Black/Blue)
      // Operation 3: Flip horizontally (still points DOWN)
      const correctAns_ar = 'مثلث أزرق موجه نحو الأسفل';
      const correctAns_en = 'Blue triangle pointing downward';

      const distractors = [
        { ar: 'مثلث أبيض موجه نحو الأسفل', en: 'White triangle pointing downward' },
        { ar: 'مثلث أزرق موجه نحو الأعلى', en: 'Blue triangle pointing upward' },
        { ar: 'مثلث أبيض موجه نحو الأعلى', en: 'White triangle pointing upward' }
      ];

      const allChoices = prng.shuffle([
        { ar: correctAns_ar, en: correctAns_en, isCorrect: true },
        ...distractors
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">الحالة الأولية: [ مثلث أبيض يشير للأعلى ]</div>
          <div class="iq-stimulus-card-body">1. تدوير 180°  ←  2. تحويل لونه إلى الأزرق  ←  3. انعكاس أفقي</div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">Initial State: [ White Triangle pointing UP ]</div>
          <div class="iq-stimulus-card-body">1. Rotate 180°  →  2. Invert color to Blue  →  3. Horizontal flip</div>
        </div>
      `;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `بتتبع العمليات المتتالية في ذاكرتك العاملة، ما هو الشكل واللون والاتجاه النهائي للرمز؟`,
        questionText_en: `Tracking the sequential operations in your working memory, what is the final shape, color, and orientation?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `التدوير 180° يجعله يشير للأسفل، وتغيير اللون يجعله أزرق، والانعكاس الأفقي لا يغير اتجاهه الرأسي، فالنتيجة: ${correctAns_ar}.`,
        explanation_en: `180° turns it downward, color shifts to blue, horizontal reflection preserves downward orientation.`
      };
    }
  },

  // 5. Cup Shell Swap Tracking
  {
    id: 'mem_token_swap_cups',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      // 3 cups: 1, 2, 3
      // Coin starts under cup 1
      // Swap 1: 1 <-> 2 (coin is now at 2)
      // Swap 2: 2 <-> 3 (coin is now at 3)
      // Swap 3: 1 <-> 2 (coin still at 3)
      const correctAns = 'الكوب رقم 3';
      const distractors = ['الكوب رقم 1', 'الكوب رقم 2', 'خارج الأكواب الثلاثة'];

      const allChoices = prng.shuffle([
        { val: correctAns, isCorrect: true },
        ...distractors.map(d => ({ val: d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">البداية: العملة المعدنية تستقر تحت [ الكوب رقم 1 ]</div>
          <div class="iq-stimulus-card-body">التبديل 1: بين (1 و 2)  ←  التبديل 2: بين (2 و 3)  ←  التبديل 3: بين (1 و 2)</div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">Initial: Coin rests under [ Cup 1 ]</div>
          <div class="iq-stimulus-card-body">Swap 1: (1 & 2)  →  Swap 2: (2 & 3)  →  Swap 3: (1 & 2)</div>
        </div>
      `;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `بعد تنفيذ التبديلات الثلاثة المتتالية لمواضع الأكواب، تحت أي كوب تستقر العملة المعدنية؟`,
        questionText_en: `After completing the three sequential cup position swaps, which cup hides the coin?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.val,
          text_en: c.val,
          value: c.val
        })),
        correctIndex,
        explanation_ar: `بعد التبديل الأول: في 2. بعد التبديل الثاني: في 3. التبديل الثالث حدث بين 1 و 2 دون تحريك 3، فاستقرت في الكوب 3.`,
        explanation_en: `Swap 1 moves coin to 2. Swap 2 moves coin to 3. Swap 3 involves 1 and 2, leaving the coin at cup 3.`
      };
    }
  },

  // 6. Associative Word Pair Recall
  {
    id: 'mem_word_pair_association',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const pairs = [
        { a_ar: 'النسر', b_ar: 'القمة', a_en: 'Eagle', b_en: 'Summit' },
        { a_ar: 'الحوت', b_ar: 'الأعماق', a_en: 'Whale', b_en: 'Abyss' },
        { a_ar: 'الغزال', b_ar: 'السهوب', a_en: 'Gazelle', b_en: 'Steppe' },
        { a_ar: 'الذئب', b_ar: 'الغابة', a_en: 'Wolf', b_en: 'Forest' }
      ];

      const chosenPairs = prng.sample(pairs, 3);
      const target = chosenPairs[prng.int(0, 2)];

      const correctAns_ar = target.b_ar;
      const correctAns_en = target.b_en;

      const otherPairs = pairs.filter(p => p.b_ar !== target.b_ar);
      const distractors = prng.sample(otherPairs, 3);

      const allChoices = prng.shuffle([
        { ar: correctAns_ar, en: correctAns_en, isCorrect: true },
        ...distractors.map(d => ({ ar: d.b_ar, en: d.b_en, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">أزواج الكلمات المقترنة بالذاكرة:</div>
          <div class="iq-stimulus-card-highlight">
            [ ${chosenPairs[0].a_ar} &nbsp;—&nbsp; ${chosenPairs[0].b_ar} ] &nbsp;•&nbsp; [ ${chosenPairs[1].a_ar} &nbsp;—&nbsp; ${chosenPairs[1].b_ar} ] &nbsp;•&nbsp; [ ${chosenPairs[2].a_ar} &nbsp;—&nbsp; ${chosenPairs[2].b_ar} ]
          </div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">Memorized Word Pairs:</div>
          <div class="iq-stimulus-card-highlight">
            [ ${chosenPairs[0].a_en} &nbsp;—&nbsp; ${chosenPairs[0].b_en} ] &nbsp;•&nbsp; [ ${chosenPairs[1].a_en} &nbsp;—&nbsp; ${chosenPairs[1].b_en} ] &nbsp;•&nbsp; [ ${chosenPairs[2].a_en} &nbsp;—&nbsp; ${chosenPairs[2].b_en} ]
          </div>
        </div>
      `;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `استناداً إلى الأزواج المقترنة المعروضة، ما هي الكلمة التي ارتبطت بكلمة (${target.a_ar})؟`,
        questionText_en: `Based on the presented paired associates, which word was paired with (${target.a_en})?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `الكلمة المقترنة بـ (${target.a_ar}) في القائمة هي: ${correctAns_ar}.`,
        explanation_en: `The word paired with (${target.a_en}) was: ${correctAns_en}.`
      };
    }
  },

  // 7. Mental Digit Span Sorting
  {
    id: 'mem_digit_ascending_sort',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      // 5 distinct numbers: e.g. [7, 2, 9, 4, 6]
      // Sorted: [2, 4, 6, 7, 9]
      // Question: Which number sits in the 3rd (middle) position when sorted in ascending order?
      const digits = prng.sample([1, 2, 3, 4, 5, 6, 7, 8, 9], 5);
      const sorted = [...digits].sort((a, b) => a - b);
      const correctVal = sorted[2]; // median element

      const distractors = sorted.filter(v => v !== correctVal).slice(0, 3);
      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-highlight" style="font-family: monospace; font-size: 1.25rem; letter-spacing: 0.1em;">
            ${digits.join(' &nbsp;•&nbsp; ')}
          </div>
        </div>
      `;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `إذا أعدت ترتيب الأرقام الخمسة المعروضة ذهنياً بترتيب تصاعدي (من الأصغر إلى الأكبر)، فما هو الرقم الذي سيستقر في المركز الثالث (المنتصف)؟`,
        questionText_en: `If the 5 numbers are sorted ascendingly in working memory, which number occupies the 3rd (median) position?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en: stimulusHtml_ar,
        options: allVals.map((v, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: String(v),
          text_en: String(v),
          value: v
        })),
        correctIndex,
        explanation_ar: `الترتيب التصاعدي الكامل: ${sorted.join(' < ')}. الرقم الأوسط في المركز الثالث هو: ${correctVal}.`,
        explanation_en: `Ascending order: ${sorted.join(' < ')}. The 3rd value is: ${correctVal}.`
      };
    }
  },

  // 8. Visual Alteration Spotting
  {
    id: 'mem_symbol_substitution_spot',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const items = [
        { name_ar: 'المربع', sym: '■' },
        { name_ar: 'الدائرة', sym: '●' },
        { name_ar: 'المثلث', sym: '▲' },
        { name_ar: 'النجمة', sym: '★' }
      ];

      const target = prng.pick(items);
      const replacement = { name_ar: 'المعين', sym: '◆' };

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-muted">المجموعة الأصلية المحفوظة: [ ■ • ● • ▲ • ★ ]</div>
          <div class="iq-stimulus-card-highlight">المجموعة المعدلة: [ ■ • ● • ◆ • ★ ]</div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-muted">Original Memorized Set: [ ■ • ● • ▲ • ★ ]</div>
          <div class="iq-stimulus-card-highlight">Modified Set: [ ■ • ● • ◆ • ★ ]</div>
        </div>
      `;

      const correctAns_ar = 'المثلث (▲) تم استبداله بـ المعين (◆)';
      const correctAns_en = 'Triangle was replaced by Diamond';

      const distractors = [
        { ar: 'الدائرة (●) تم استبدالها بـ المعين (◆)', en: 'Circle was replaced by Diamond' },
        { ar: 'المربع (■) تم استبداله بـ المعين (◆)', en: 'Square was replaced by Diamond' },
        { ar: 'النجمة (★) تم استبدالها بـ المعين (◆)', en: 'Star was replaced by Diamond' }
      ];

      const allChoices = prng.shuffle([
        { ar: correctAns_ar, en: correctAns_en, isCorrect: true },
        ...distractors
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `بمقارنة المجموعتين في ذاكرتك، أي رمز تم حذفه واستبداله بالرمز الجديد؟`,
        questionText_en: `Comparing both sets in working memory, which original symbol was substituted?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `المثلث (▲) كان في الموضع الثالث وتم استبداله بالمعين (◆).`,
        explanation_en: `Triangle was located in the third position and got replaced with Diamond.`
      };
    }
  },

  // 9. N-Back 2-Step Back Match
  {
    id: 'mem_n_back_dual_match',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      // Sequence: K - M - K - T - L
      // Step 3 (K) matches step 1 (K) 2-steps back!
      const correctAns_ar = 'الحرف (K) في الموضع الثالث طابق الموضع الأول';
      const correctAns_en = 'Letter K at step 3 matched step 1';

      const distractors = [
        { ar: 'الحرف (T) في الموضع الرابع طابق الموضع الثاني', en: 'Letter T at step 4 matched step 2' },
        { ar: 'الحرف (L) في الموضع الخامس طابق الموضع الثالث', en: 'Letter L at step 5 matched step 3' },
        { ar: 'الحرف (M) في الموضع الثاني طابق الموضع الرابع', en: 'Letter M at step 2 matched step 4' }
      ];

      const allChoices = prng.shuffle([
        { ar: correctAns_ar, en: correctAns_en, isCorrect: true },
        ...distractors
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-highlight" style="font-family: monospace; font-size: 1.15rem; letter-spacing: 0.05em;">
            1: [ K ] &nbsp;→&nbsp; 2: [ M ] &nbsp;→&nbsp; 3: [ K ] &nbsp;→&nbsp; 4: [ T ] &nbsp;→&nbsp; 5: [ L ]
          </div>
        </div>
      `;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في اختبار الذاكرة التراجعية (2-Back)، أي عنصر تكرر وتطابق تماماً مع العنصر الذي سبقه بخطوتين؟`,
        questionText_en: `In the 2-Back memory paradigm, which letter matched the item that appeared exactly 2 steps prior?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en: stimulusHtml_ar,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `الحرف K في الخطوة 3 تكرر بعد ظهوره في الخطوة 1، بفارق خطوتين بالتمام.`,
        explanation_en: `Letter K at position 3 matches position 1 with an exact 2-step lag.`
      };
    }
  },

  // 10. Temporal Order of Timestamped Events
  {
    id: 'mem_temporal_timeline_order',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const events = [
        { t: '09:00', name_ar: 'إرسال التقرير المالي', name_en: 'Send financial report' },
        { t: '10:30', name_ar: 'عقد الاجتماع التنسيقي', name_en: 'Hold coordination meeting' },
        { t: '13:00', name_ar: 'اعتماد الموازنة السنوية', name_en: 'Approve annual budget' },
        { t: '15:30', name_ar: 'توقيع العقد الإنشائي', name_en: 'Sign construction contract' }
      ];

      const targetIdx = 2; // 'اعتماد الموازنة السنوية'
      const priorEvent = events[targetIdx - 1]; // 'عقد الاجتماع التنسيقي'

      const correctAns_ar = priorEvent.name_ar;
      const correctAns_en = priorEvent.name_en;

      const otherEvents = events.filter(e => e.name_ar !== priorEvent.name_ar);
      const distractors = otherEvents.map(e => ({ ar: e.name_ar, en: e.name_en }));

      const allChoices = prng.shuffle([
        { ar: correctAns_ar, en: correctAns_en, isCorrect: true },
        ...distractors
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">الجدول الزمني للأحداث اليومية:</div>
          <div class="iq-stimulus-card-highlight">
            09:00 تقرير &nbsp;←&nbsp; 10:30 اجتماع &nbsp;←&nbsp; 13:00 اعتماد &nbsp;←&nbsp; 15:30 توقيع
          </div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">Timeline of Daily Events:</div>
          <div class="iq-stimulus-card-highlight">
            09:00 Report &nbsp;→&nbsp; 10:30 Meeting &nbsp;→&nbsp; 13:00 Approval &nbsp;→&nbsp; 15:30 Contract
          </div>
        </div>
      `;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هو الحدث الذي وقع مباشرة قبل حدث (${events[targetIdx].name_ar}) في الجدول الزمني؟`,
        questionText_en: `Which event occurred immediately prior to (${events[targetIdx].name_en})?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `الحدث الذي سبق الاعتماد مباشرة في الساعة 10:30 هو: ${correctAns_ar}.`,
        explanation_en: `The event preceding approval was: ${correctAns_en}.`
      };
    }
  },

  // 11. Categorical Mental Inventory Count
  {
    id: 'mem_category_inventory_count',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      // List of items: 4 Fruits, 3 Tools, 2 Animals
      // [تفاح, مطرقة, عنب, مفك, برتقال, موز, كماشة]
      const correctVal = 4; // number of fruits
      const distSet = new Set([3, 5, 2]);
      const distractors = Array.from(distSet);

      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">قائمة العناصر المحفوظة في الذاكرة:</div>
          <div class="iq-stimulus-card-highlight">
            [ تفاح &nbsp;•&nbsp; مطرقة &nbsp;•&nbsp; عنب &nbsp;•&nbsp; مفك براغي &nbsp;•&nbsp; برتقال &nbsp;•&nbsp; موز &nbsp;•&nbsp; كماشة ]
          </div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">Memorized Inventory List:</div>
          <div class="iq-stimulus-card-highlight">
            [ Apple &nbsp;•&nbsp; Hammer &nbsp;•&nbsp; Grape &nbsp;•&nbsp; Screwdriver &nbsp;•&nbsp; Orange &nbsp;•&nbsp; Banana &nbsp;•&nbsp; Pliers ]
          </div>
        </div>
      `;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `كم عدد العناصر التي تنتمي تصنيفياً إلى فئة (الفواكه) ضمن القائمة المحفوظة؟`,
        questionText_en: `How many items belong to the (Fruits) category in the memorized list?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options: allVals.map((v, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: `${v} عناصر`,
          text_en: `${v} items`,
          value: v
        })),
        correctIndex,
        explanation_ar: `عناصر الفواكه هي: (تفاح، عنب، برتقال، موز) ومجموعها 4 عناصر.`,
        explanation_en: `The fruit items are apple, grape, orange, banana, totaling 4 items.`
      };
    }
  },

  // 12. Letter Alphabet Skip Distance
  {
    id: 'mem_alphabet_skip_gap',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      // Letters: A - C - E - G (skipping 1 letter each time: B, D, F)
      const correctAns = 'I';
      const distractors = ['H', 'J', 'K'];

      const allChoices = prng.shuffle([correctAns, ...distractors]);
      const correctIndex = allChoices.indexOf(correctAns);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-highlight" style="font-family: monospace; font-size: 1.25rem; letter-spacing: 0.1em;">
            A &nbsp;•&nbsp; C &nbsp;•&nbsp; E &nbsp;•&nbsp; G &nbsp;•&nbsp; [ ? ]
          </div>
        </div>
      `;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `بتخطي حرف أبجدي واحد بالتناوب بين كل عنصر والآخر، ما هو الحرف التالي في المتتالية؟`,
        questionText_en: `Skipping one letter in the alphabet alternately, what is the next letter?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en: stimulusHtml_ar,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c,
          text_en: c,
          value: c
        })),
        correctIndex,
        explanation_ar: `المتتالية تقفز بحرفين (+2): A (+2) -> C (+2) -> E (+2) -> G (+2) -> I.`,
        explanation_en: `Sequence increments by +2 alphabet positions, leading to I.`
      };
    }
  },

  // 13. Interleaved Character Number Unscramble
  {
    id: 'mem_interleaved_stream',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      // Stream: X - 1 - Y - 2 - Z - 3
      const correctAns = 'Z';
      const distractors = ['X', 'Y', '3'];

      const allChoices = prng.shuffle([correctAns, ...distractors]);
      const correctIndex = allChoices.indexOf(correctAns);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-highlight" style="font-family: monospace; font-size: 1.2rem; letter-spacing: 0.05em;">
            [ X , 1 , Y , 2 , Z , 3 ]
          </div>
        </div>
      `;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في التسلسل المتداخل بين الحروف والأرقام، ما هو العنصر الذي احتل المركز الخامس بالضبط؟`,
        questionText_en: `In the interleaved stream, which element occupied the 5th exact position?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en: stimulusHtml_ar,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c,
          text_en: c,
          value: c
        })),
        correctIndex,
        explanation_ar: `العناصر حسب ترتيبها: 1: X، 2: 1، 3: Y، 4: 2، 5: Z. العنصر الخامس هو Z.`,
        explanation_en: `The 5th indexed token in the sequence is Z.`
      };
    }
  },

  // 14. Mental Rotation Orientation Memory
  {
    id: 'mem_clock_hand_rotation_target',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      // Compass pointing North. Rotate 90° CW -> East. Rotate 180° -> West. Rotate 45° CCW -> South-West.
      const correctAns_ar = 'الجنوب الغربي (South-West)';
      const correctAns_en = 'South-West';

      const distractors = [
        { ar: 'الشمال الشرقي (North-East)', en: 'North-East' },
        { ar: 'الجنوب الشرقي (South-East)', en: 'South-East' },
        { ar: 'الشمال الغربي (North-West)', en: 'North-West' }
      ];

      const allChoices = prng.shuffle([
        { ar: correctAns_ar, en: correctAns_en, isCorrect: true },
        ...distractors
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">البوصلة تشير في البداية نحو [ الشمال ]</div>
          <div class="iq-stimulus-card-body">1. دوران 90° مع عقارب الساعة  ←  2. دوران 180°  ←  3. دوران 45° عكس عقارب الساعة</div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">Compass initially points [ North ]</div>
          <div class="iq-stimulus-card-body">1. Rotate 90° Clockwise  →  2. Rotate 180°  →  3. Rotate 45° Counter-Clockwise</div>
        </div>
      `;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `بتتبع تدوير مؤشر البوصلة في ذهنك، إلى أي جهة يشير المؤشر في النهاية؟`,
        questionText_en: `Tracking the compass pointer rotations mentally, which direction does it face finally?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `شمال (0°) + 90° = شرق (90°). + 180° = غرب (270°). - 45° = 225° وهو اتجاه الجنوب الغربي.`,
        explanation_en: `North (0°) + 90° -> East. + 180° -> West (270°). - 45° -> 225° (South-West).`
      };
    }
  },

  // 15. Sequence Length Span Bound
  {
    id: 'mem_span_capacity_check',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 30,
    generate(prng, difficulty = 2) {
      const items = ['7', '3', '8', '2', '9', '5', '1'];
      const correctVal = items.length;

      const distSet = new Set([6, 8, 5]);
      const distractors = Array.from(distSet);

      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-highlight" style="font-family: monospace; font-size: 1.25rem; letter-spacing: 0.1em;">
            ${items.join(' &nbsp;•&nbsp; ')}
          </div>
        </div>
      `;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `كم عدد الأرقام الفردية التي تضمنتها هذه السلسلة العددية المعروضة بالكامل؟`,
        questionText_en: `How many total individual digits were contained in the presented sequence?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en: stimulusHtml_ar,
        options: allVals.map((v, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: `${v} أرقام`,
          text_en: `${v} digits`,
          value: v
        })),
        correctIndex,
        explanation_ar: `السلسلة تضم 7 أرقام متتالية مطابقة لمتوسط سعة الذاكرة العاملة (سحر الرقم 7 ± 2).`,
        explanation_en: `The sequence contains 7 items, matching Miller's magical memory span 7 +/- 2.`
      };
    }
  }
];
